// https://leeoniya.github.io/uFuzzy/demos/compare.html?libs=uFuzzy,fuzzysort,QuickScore,Fuse&search=super%20ma
// https://leeoniya.github.io/uFuzzy/demos/compare.html?libs=fuzzysort&search=super%20ma
// https://leeoniya.github.io/uFuzzy/demos/compare.html?libs=fuzzyMatch&search=super%20ma
// https://github.com/fwextensions/quick-score
// https://github.com/grafana/grafana/blob/main/packages/grafana-ui/src/utils/fuzzy.ts
// https://github.com/farzher/fuzzysort/blob/master/fuzzysort.js

export type createSearchRes<T extends Record<string, unknown>> = {
  search: (q: string) => T[]
  items: T[]
  size: number
}

type FuzzyMatch = {
  /**
   * Total number of unmatched letters between matched letters
   */
  distance: number
  found: boolean
}

/**
 * Attempts to do a partial input search, e.g. allowing to search for a text (needle)
 * in another text (stack) by skipping some letters in-between. All letters from
 * the needle must exist in the stack in the same order to find a match.
 *
 * The search is case sensitive. Convert stack and needle to lower case
 * to make it case insensitive.
 *
 * @param stack - main text to be searched
 * @param needle - partial text to find in the stack
 *
 * @internal
 */
function fuzzyMatch(stack: string, needle: string): FuzzyMatch {
  // https://github.com/grafana/grafana/blob/main/packages/grafana-ui/src/utils/fuzzy.ts

  let distance = 0
  let searchIndex = stack.indexOf(needle)
  // Remove whitespace from needle as a temporary solution to treat separate string
  // queries as 'AND'
  // needle = needle

  // const ranges: HighlightPart[] = []

  if (searchIndex !== -1) {
    return { distance: 0, found: true }
  }

  for (const letter of needle) {
    const letterIndex = stack.indexOf(letter, searchIndex)

    if (letterIndex === -1) {
      return { distance: Number.POSITIVE_INFINITY, found: false }
    }
    // do not cumulate the distance if it's the first letter
    if (searchIndex !== -1) {
      distance += letterIndex - searchIndex
    }
    searchIndex = letterIndex + 1
  }

  return { distance, found: true }
}

type InputSearchOn = (string | None) | (string | None)[]

type SearchFuncitonArgs2<T extends Record<string, unknown>> = {
  searchType?: () => 'exact' | 'fuzzy'
  query: () => string | None
  items: () => T[]
  searchOn: (item: T) => InputSearchOn
}

const alphaReplace: Record<string, string> = {
  ą: 'a',
  č: 'c',
  ę: 'e',
  ė: 'e',
  į: 'i',
  š: 's',
  ų: 'u',
  ū: 'u',
  ž: 'z',
}

// const badLetters = Object.keys(alpha)

function fixWord(input: string) {
  let word = ''
  for (let index = 0; index < input.length; index++) {
    const letter = input.charAt(index)
    // else  .trim() .replace(/\s/g, '')
    if (letter !== ' ') word += alphaReplace[letter] || letter
  }
  return word
}

function toSearchValue(word: string | None) {
  if (!word?.length) return
  // trim, lowrcase, remove lt letter, remove white space
  return fixWord(word.toLowerCase())
}

function toSearchWord(input: InputSearchOn): string | None {
  if (!Array.isArray(input)) return input

  let res = ''
  ;(Array.isArray(input) ? input : [input]).forEach((word) => {
    if (word?.length) res += word
  })
  return res
}

// Vuejs api
export function useWeSearch2<T extends Record<string, unknown>>(args: SearchFuncitonArgs2<T>): {
  itemsAll: globalThis.ComputedRef<T[]>
  items: globalThis.ComputedRef<T[]>
  find: (cb: (i: T) => boolean) => T | undefined
} {
  const itemsAll = computed(() => helpers.arr(args.items()))
  type SearchDoc = { doc: T; search: string }
  const searchArr = shallowRef<SearchDoc[]>([])

  watch(
    () => itemsAll.value,
    (arr) => {
      searchArr.value = helpers.arr(arr, (doc): SearchDoc | None => {
        const search = toSearchValue(toSearchWord(args.searchOn(doc)))
        if (!search?.length) return
        return { search, doc }
      })
    },
    { immediate: true },
  )

  const items = computed((): T[] => {
    const query = toSearchValue(args.query())

    if (typeof query !== 'string' || !query.length) return itemsAll.value
    if (args.searchType?.() === 'exact') {
      const regex = new RegExp(`^${query}`, 'i')
      return helpers.arr(searchArr.value, (item) => regex.test(item.search) ? item.doc : undefined)
    }

    const results: { doc: T; sort: FuzzyMatch['distance'] }[] = []

    searchArr.value.forEach((doc) => {
      const res = fuzzyMatch(doc.search, query)
      if (res.found) results.push({ doc: doc.doc, sort: res.distance })
    })

    return results.sort((a, b) => a.sort - b.sort).map((i) => i.doc)
  })

  function find(cb: (i: T) => boolean) {
    return itemsAll.value.find((i) => cb(i))
  }

  return { itemsAll, items, find }
}
